import { useJSONFetcher, JSONFetcherOptions } from "@outplayed/json-fetcher";
import { useUGGApiVersions } from "../ugg-api-versions";
import { champion_transform_map } from "@ugg/shared/utils/multi-build-helpers";
import { getApiRoot } from "../../api-helpers";
import { useValidateQueryParams } from "@ugg/shared/query-params/params-helpers";
import { validParams } from "@ugg/shared/query-params/valid-params";
import { cleanRankingsData, RankingStatsInfo } from "@ugg/shared/api/data-parser/champions/ranking-stats";
import { CHAMPION_PAGES } from "@ugg/shared/pages/champion-pages";
import { QueueTypeS } from "@ugg/shared/utils/queue-type-helpers";
import { getKey } from "@ugg/shared/api/data-parser/champions/common";
import { RANKINGS_EMERALD_PLUS_WORLD } from "@ugg/shared/constants/constants";
import { RegionS } from "@ugg/shared/utils/region-helpers";
import { RankS } from "@ugg/shared/utils/rank-helpers";

import { RankingStats } from "@ugg/shared/interfaces/champions/ranking-stats.interface";

// ARAM Mayhem doesn't flow through the normal ranking-stats backend
// — it has its own data pipeline that produces a static tier list
// JSON. See the module-level comment in aram-mayhem-ranking-stats.ts
// for the full story. The dispatcher `useChampionRankingStats` at
// the bottom of this file picks between the original hook (below)
// and the ARAM Mayhem variant based on queueType.
import { useChampionRankingStatsAramMayhem } from "./aram-mayhem-ranking-stats";

export function useChampionRankingStatsURL(championId: number, options: { queueType?: string; patch?: string }) {
  let { queueType, patch } = options;
  const { data: version } = useUGGApiVersions({ apiKey: "rankings", patch: options.patch });

  // backend saves to /urf/ url instead of /arurf/
  if (queueType === QueueTypeS.ARURF) {
    queueType = QueueTypeS.URF;
  }

  const root = getApiRoot(options.patch);
  return [`${root}/rankings`, patch, queueType, championId, `${version}.json`].join("/");
}

// ─────────────────────────────────────────────────────────────────────
// Original ranking-stats hook — renamed for the ARAM Mayhem dispatch
// refactor. Body is byte-identical to the previous
// useChampionRankingStats; only the function name changed. Callers
// that need the original behavior explicitly (bypassing the ARAM
// Mayhem dispatcher below) can import this directly.
// ─────────────────────────────────────────────────────────────────────
export function useChampionRankingStatsOriginal(
  championId: number,
  options?: { params?: Record<string, any> } & JSONFetcherOptions<RankingStats, ReturnType<typeof cleanRankingsData>>,
) {
  const { params = {}, ssr = false, skip = false } = options || {};
  const validateQueryParams = useValidateQueryParams(validParams);
  const validatedQueryParams = validateQueryParams(CHAMPION_PAGES.OVERVIEW, params, true);

  const rankingStatsURL = useChampionRankingStatsURL(championId, validatedQueryParams);
  const recCacheKey = `${RANKINGS_EMERALD_PLUS_WORLD}::${rankingStatsURL}`;
  return useJSONFetcher<RankingStats, ReturnType<typeof cleanRankingsData>>(rankingStatsURL, {
    ssr,
    skip: skip,
    onCompleted: (url, json, cacheKey) => {
      const cleanData = cleanRankingsData(json);

      const dataKey = getKey(validatedQueryParams.region, validatedQueryParams.rank, "");

      if (cacheKey.match(recCacheKey)) {
        return Object.fromEntries(Object.entries(cleanData || {}).filter(([key, value]) => key.match(dataKey)));
      }

      return cleanData;
    },
    customCacheKey:
      validatedQueryParams.region === RegionS.WORLD &&
      validatedQueryParams.rank === RankS.EMERALD_PLUS &&
      validatedQueryParams.queueType === QueueTypeS.RANKED_SOLO
        ? recCacheKey
        : "",
  });
}

// ─────────────────────────────────────────────────────────────────────
// Top-level dispatcher — routes to useChampionRankingStatsAramMayhem
// when the queue is ARAM Mayhem, otherwise to
// useChampionRankingStatsOriginal.
//
// Both sub-hooks are ALWAYS called (React rules-of-hooks — the
// order of hook calls must be stable across renders), but only one
// actually issues a network request; the other has `skip: true`
// and returns its idle state. Small memory overhead per render,
// zero network overhead for the skipped branch.
//
// validateQueryParams runs three times per invocation (once here,
// once in each sub-hook). It's a synchronous normalization pass
// with no I/O — negligible cost. Extracting it out would require
// changing both sub-hooks' signatures to accept pre-validated
// params, which spreads the refactor further than warranted.
// ─────────────────────────────────────────────────────────────────────
export function useChampionRankingStats(
  championId: number,
  options?: { params?: Record<string, any> } & JSONFetcherOptions<RankingStats, ReturnType<typeof cleanRankingsData>>,
) {
  const { params = {}, skip = false } = options || {};
  const validateQueryParams = useValidateQueryParams(validParams);
  const validatedQueryParams = validateQueryParams(CHAMPION_PAGES.OVERVIEW, params, true);
  const isAramMayhem = validatedQueryParams.queueType === QueueTypeS.ARAM_MAYHEM;

  // Always call BOTH sub-hooks — rules of hooks require stable
  // call order across renders. The wrong-branch hook gets
  // `skip: true` and short-circuits before touching the network.
  const originalResult = useChampionRankingStatsOriginal(championId, {
    ...options,
    skip: skip || isAramMayhem,
  });
  const aramMayhemResult = useChampionRankingStatsAramMayhem(championId, {
    ...options,
    skip: skip || !isAramMayhem,
  });

  return isAramMayhem ? aramMayhemResult : originalResult;
}

export function useMultipleChampionRankingStats(championId: number, options?: { params?: Record<string, any>; ssr?: boolean }) {
  const { params = {}, ssr = false } = options || {};

  const res: Record<
    number,
    {
      rankingStats: Record<string, RankingStatsInfo>;
      fetchingRankingStats: boolean;
      errorRankingStats: Error | null;
    }
  > = {};
  const maxBuilds = Object.values(champion_transform_map).reduce((acc, curr) => {
    return Math.max(curr.length, acc);
  }, 1);
  const baseArray = Array(maxBuilds).fill(0);
  const champions = champion_transform_map[championId]
    ? champion_transform_map[championId].concat(baseArray.slice(champion_transform_map[championId].length))
    : [championId].concat(baseArray.slice(1));

  champions.map((championId) => {
    let {
      data: rankingStats,
      loading: fetchingRankingStats,
      error: errorRankingStats,
    } = useChampionRankingStats(championId, { ssr, params });

    res[championId] = { rankingStats, fetchingRankingStats, errorRankingStats };
  });

  return res;
}
